import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

// ProductImage component with fallback
const ProductImage: React.FC<{ src: string; alt: string; fallbackColor?: string }> = ({ src, alt, fallbackColor }) => {
  const [error, setError] = React.useState(false);

  return (
    <div className="w-20 h-20 rounded flex items-center justify-center overflow-hidden"
      style={{ backgroundColor: fallbackColor || '#F7F7F7' }}>
      {!error ? (
        <img
          src={src}
          alt={alt}
          className="w-full h-full object-cover"
          onError={() => setError(true)}
        />
      ) : (
        <span className="text-xs text-[#767676] text-center px-1">
          {alt.split(' ').slice(0, 2).join(' ')}
        </span>
      )}
    </div>
  );
};

// Lululemon products for display - using reliable CDN URLs
const CACHE_BUST = Date.now();
const LULULEMON_PRODUCTS = [
  {
    id: 'align',
    name: 'Align High-Rise Pant 25"',
    price: '$98',
    image: `/assets/images/lululemon/align-pant.jpg?v=${CACHE_BUST}`,
    description: 'Buttery-soft Nulu™ fabric',
    tag: 'Best for Yoga',
  },
  {
    id: 'wunder',
    name: 'Wunder Train High-Rise Tight 28"',
    price: '$98',
    image: `/assets/images/lululemon/wunder-train.jpg?v=${CACHE_BUST}`,
    description: 'Everlux™ quick-drying fabric',
    tag: 'Best for Training',
  },
  {
    id: 'scuba',
    name: 'Scuba Oversized Half-Zip',
    price: '$118',
    image: `/assets/images/lululemon/scuba-hoodie.jpg?v=${CACHE_BUST}`,
    description: 'Cozy cotton fleece',
    tag: 'Guest Favorite',
  },
];

const LululemonDemoView: React.FC = () => {
  const navigate = useNavigate();
  const [isHovered, setIsHovered] = useState(false);

  const handleTakeback = () => {
    navigate('/lululemon/takeback');
  };

  const handleLoginAsSarah = () => {
    // Open voice assistant locally for demo testing
    const isLocal = window.location.hostname === 'localhost';
    const voiceBaseUrl = isLocal ? 'http://localhost:3002' : 'https://lululemon.voice.pentatonic.com';
    const voiceUrl = `${voiceBaseUrl}?session=sarah-demo&store=short-hills-nj&customer=sarah`;
    window.open(voiceUrl, '_blank');
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Header */}
      <header className="bg-[#D31334] text-white py-4 px-8 flex items-center justify-between">
        <div className="flex items-center gap-4">
          {/* Lululemon logo */}
          <img 
            src="/assets/images/logos/lululemon-white.png" 
            alt="lululemon" 
            className="h-6"
            onError={(e) => {
              // Fallback to text if image fails
              const target = e.target as HTMLImageElement;
              target.style.display = 'none';
            }}
          />
          <span className="text-sm opacity-80">Short Hills, NJ</span>
        </div>
        <div className="text-sm">
          AI Educator Demo
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex">
        {/* Left side - Welcome & QR with background image */}
        <div 
          className="w-1/2 relative flex flex-col items-center justify-center p-12"
          style={{
            backgroundImage: 'url(/assets/images/lululemon/lululemon-qr-background.jpg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        >
          {/* Dark overlay for text readability */}
          <div className="absolute inset-0 bg-black/40" />
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="relative z-10 text-center max-w-md"
          >
            <h1 className="text-4xl font-semibold text-white mb-4">
              Hello
            </h1>
            <p className="text-lg text-white/80 mb-8">
              Connect your phone to chat with our AI Educator. 
              Get personalized recommendations, check inventory, and find products in-store.
            </p>

            {/* QR Code */}
            <div className="bg-white p-6 rounded-lg shadow-lg inline-block mb-6">
              <img
                src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent('https://lululemon.voice.pentatonic.com?session=guest-' + Date.now() + '&store=short-hills-nj')}`}
                alt="Scan to connect"
                className="w-48 h-48"
              />
            </div>
            <p className="text-sm text-white/70">
              Scan with your phone camera
            </p>

            {/* Demo Login Button */}
            <motion.button
              onClick={handleLoginAsSarah}
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="mt-8 bg-[#D31334] text-white px-8 py-4 rounded font-semibold uppercase tracking-wider shadow-lg hover:bg-[#B01030] transition-colors"
            >
              {isHovered ? '→ Start Demo as Sarah' : 'Log in as Sarah'}
            </motion.button>
            <p className="text-xs text-white/50 mt-2">
              Demo: Opens voice chat with personalized experience
            </p>
          </motion.div>
        </div>

        {/* Right side - Featured Products */}
        <div className="w-1/2 bg-white flex flex-col p-8">
          <h2 className="text-2xl font-semibold text-[#1A1A1A] mb-6">
            Featured Products
          </h2>
          <p className="text-[#767676] mb-6">
            Ask our AI Educator about any of these products or anything else in store!
          </p>

          <div className="space-y-4 flex-1">
            {LULULEMON_PRODUCTS.map((product, index) => (
              <motion.div
                key={product.id}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                className="flex items-center gap-4 bg-[#F7F7F7] rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer"
              >
                <ProductImage 
                  src={product.image}
                  alt={product.name}
                  fallbackColor={product.id === 'align' ? '#1A1A1A' : product.id === 'wunder' ? '#2A2A2A' : undefined}
                />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-[#1A1A1A]">{product.name}</h3>
                    <span className="text-xs bg-[#D31334] text-white px-2 py-0.5 rounded">
                      {product.tag}
                    </span>
                  </div>
                  <p className="text-sm text-[#767676]">{product.description}</p>
                  <p className="text-lg font-bold text-[#D31334] mt-1">{product.price}</p>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Like New Trade-In CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.4 }}
            onClick={handleTakeback}
            className="mt-6 bg-gradient-to-r from-[#1A1A1A] to-[#2A2A2A] rounded-lg p-6 cursor-pointer hover:shadow-lg transition-shadow"
          >
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <svg className="w-6 h-6 text-[#22C55E]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  <span className="text-[#22C55E] font-semibold uppercase tracking-wider text-sm">Like New</span>
                </div>
                <h3 className="text-white text-xl font-semibold mb-1">Trade-In Your Gear</h3>
                <p className="text-[#A0A0A0] text-sm">Earn up to 50% credit on gently used lululemon</p>
              </div>
              <div className="text-white">
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </div>
          </motion.div>

          {/* Bottom info */}
          <div className="mt-6 pt-6 border-t border-[#E5E5E5]">
            <div className="flex items-center gap-4 text-sm text-[#767676]">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                AI Assistant Online
              </div>
              <div>|</div>
              <div>Inventory: Real-time</div>
              <div>|</div>
              <div>Store Layout: Mapped</div>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-[#1A1A1A] text-white py-3 px-8 flex items-center justify-between text-sm">
        <div>Powered by Pentatonic AI</div>
        <div className="flex items-center gap-6">
          <button 
            onClick={handleTakeback}
            className="text-[#22C55E] hover:text-white transition-colors font-medium"
          >
            Like New Trade-In
          </button>
          <span>|</span>
          <span>Free Hemming</span>
          <span>|</span>
          <span>Complimentary Classes</span>
        </div>
      </footer>
    </div>
  );
};

export default LululemonDemoView;

